class SocialFloating extends HTMLElement {
  constructor() {
    super();

    if (window.location.pathname === '/challenge') return;

    this.classes = { activeClass: 'is-active', closingClass: 'is-closing' };
    this.onButtonClick = this.onButtonClick.bind(this);
    this.querySelectorAll('[data-social-toggle]').forEach((button) => {
      button.addEventListener('click', this.onButtonClick);
    });
  }

  onButtonClick(event) {
    event.preventDefault();
    if (!theme.config.mqlSmall) return;

    this.classList.contains(this.classes.activeClass) ? this.close() : this.open();
  }

  open() {
    this.classList.add(this.classes.activeClass);
    document.querySelector('promo-popup')?.close();
  }

  close() {
    this.classList.add(this.classes.closingClass);
    window.setTimeout(() => {
      this.classList.remove(this.classes.activeClass, this.classes.closingClass);
    }, 500);
  }
}

if (!customElements.get('social-floating')) {
  customElements.define('social-floating', SocialFloating);
}

class PromoPopup extends HTMLElement {
  constructor() {
    super();

    this.initialized = false;
    this.closeTimer = null;
    this.imageTimer = null;
    this.openTimer = null;
    this.successTimer = null;
    this.previousFocus = null;
    this.bodyLockOwned = false;
    this.bodyClassOwned = false;
    this.onButtonClick = this.onButtonClick.bind(this);
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.onDocumentFocusIn = this.onDocumentFocusIn.bind(this);
    this.onSectionLoad = this.onSectionLoad.bind(this);
    this.onSectionSelect = this.onSectionSelect.bind(this);
    this.onSectionDeselect = this.onSectionDeselect.bind(this);

  }

  connectedCallback() {
    if (window.location.pathname === '/challenge') return;

    if (!this.initialized) {
      this.cookieName = `beyours:promo-popup:${this.dataset.sectionId || 'default'}`;
      this.dismissedCookieName = `${this.cookieName}:dismissed`;
      this.submittedCookieName = `${this.cookieName}:submitted`;
      this.classes = {
        bodyClass: 'promo-popup--open',
        lockClass: 'newsletter-popup--open',
        activeClass: 'is-active',
        closingClass: 'is-closing',
        showImage: 'show-image',
      };
      this.popup = this.querySelector('.promo-popup');
      this.dialog = this.querySelector('[role="dialog"]');
      this.alwaysShow = this.dataset.frequency === 'always';
      this.querySelectorAll('[data-popup-toggle]').forEach((button) => {
        button.addEventListener('click', this.onButtonClick);
      });
      this.initializeNewsletterForms();
      this.initialized = true;
      this.initializeVisibility();
    }

    if (!window.Shopify?.designMode || this.designModeListenersAttached) return;

    this.designModeListenersAttached = true;
    document.addEventListener('shopify:section:load', this.onSectionLoad);
    document.addEventListener('shopify:section:select', this.onSectionSelect);
    document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
  }

  initializeVisibility() {
    const message = this.querySelector('.form__message, .popup-newsletter__success');
    const testing = this.dataset.testMode === 'true';

    if (message) {
      this.open();
      message.focus({ preventScroll: true });
      if (this.querySelector('.popup-newsletter__success')) {
        this.setSubmittedCookie();
        this.successTimer = window.setTimeout(() => this.close(true), 6000);
      }
      return;
    }

    const eligible = (!this.getCookie(this.submittedCookieName) && !this.getCookie(this.dismissedCookieName)) || testing;
    const withinFrequency = this.alwaysShow || !this.getCookie(this.cookieName) || testing;
    if (eligible && withinFrequency) this.init();
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:section:load', this.onSectionLoad);
    document.removeEventListener('shopify:section:select', this.onSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
    this.designModeListenersAttached = false;
    this.removeDialogListeners();
    [this.closeTimer, this.imageTimer, this.openTimer, this.successTimer].forEach((timer) => {
      if (timer !== null) window.clearTimeout(timer);
    });
    this.unlockBody();
    this.restoreFocus();
  }

  onSectionLoad(event) {
    this.handleDesignModeEvent(event, () => this.open());
  }

  onSectionSelect(event) {
    this.handleDesignModeEvent(event, () => this.open());
  }

  onSectionDeselect(event) {
    this.handleDesignModeEvent(event, () => this.close());
  }

  handleDesignModeEvent(event, callback) {
    if (typeof filterShopifyEvent === 'function') {
      filterShopifyEvent(event, this, callback);
      return;
    }

    if (event.target === this || event.target.contains?.(this)) callback();
  }

  initializeNewsletterForms() {
    this.querySelectorAll('.popup-newsletter__form').forEach((form) => {
      const birthdateInput = form.querySelector('[data-popup-birthdate]');
      const phoneInput = form.querySelector('[data-popup-phone]');
      const tagsInput = form.querySelector('[data-popup-tags]');

      birthdateInput?.addEventListener('input', (event) => {
        const digits = (event.target.value || '').replace(/[^0-9]/g, '').slice(0, 8);
        let value = digits;
        if (digits.length > 4) value = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
        else if (digits.length > 2) value = `${digits.slice(0, 2)}/${digits.slice(2)}`;
        event.target.value = value;
      });

      phoneInput?.addEventListener('input', (event) => {
        const digits = (event.target.value || '').replace(/[^0-9]/g, '').slice(0, 11);
        let value = digits;
        if (digits.length > 6) {
          value = `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
        } else if (digits.length > 2) {
          value = `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
        } else if (digits.length > 0) {
          value = `(${digits}`;
        }
        event.target.value = value;
      });

      form.addEventListener('submit', () => {
        if (tagsInput) tagsInput.value = tagsInput.value || 'newsletter, popup-cupom-15-off';
      });
    });
  }

  init() {
    if (!this.popup) return;

    if (window.Shopify?.designMode) {
      this.open();
      return;
    }

    const delay = Math.max(0, Number.parseInt(this.dataset.delay, 10) || 0);
    this.openTimer = window.setTimeout(() => this.open(), delay * 1000);
  }

  onButtonClick(event) {
    event.preventDefault();
    this.popup?.classList.contains(this.classes.activeClass) ? this.close(true) : this.open();
  }

  open() {
    if (!this.popup) return;
    if (this.popup.classList.contains(this.classes.activeClass) && !this.popup.classList.contains(this.classes.closingClass)) return;

    if (this.closeTimer !== null) window.clearTimeout(this.closeTimer);
    if (this.openTimer !== null) window.clearTimeout(this.openTimer);
    this.closeTimer = null;
    this.openTimer = null;
    this.previousFocus = document.activeElement;
    if (!document.body.classList.contains(this.classes.lockClass)) {
      document.body.classList.add(this.classes.lockClass);
      this.bodyLockOwned = true;
    }
    if (this.popup.dataset.position === 'center' && typeof setScrollbarWidth === 'function') {
      setScrollbarWidth();
    }
    if (this.popup.dataset.position === 'center' && !document.body.classList.contains(this.classes.bodyClass)) {
      document.body.classList.add(this.classes.bodyClass);
      this.bodyClassOwned = true;
    }
    this.popup.classList.remove(this.classes.closingClass);
    this.popup.classList.add(this.classes.activeClass);
    this.dialog?.setAttribute('aria-hidden', 'false');
    document.addEventListener('keydown', this.onDocumentKeydown);
    document.addEventListener('focusin', this.onDocumentFocusIn);

    if (this.popup.dataset.position === 'center') {
      this.imageTimer = window.setTimeout(() => this.popup?.classList.add(this.classes.showImage), 600);
    }

    const socialFloating = document.querySelector('social-floating');
    socialFloating?.close();
    this.querySelector('.popup-close')?.focus({ preventScroll: true });
  }

  close(userDismissal = false) {
    if (!this.popup) return;

    if (this.openTimer !== null) window.clearTimeout(this.openTimer);
    if (this.imageTimer !== null) window.clearTimeout(this.imageTimer);
    if (this.successTimer !== null) window.clearTimeout(this.successTimer);
    this.openTimer = null;
    this.imageTimer = null;
    this.successTimer = null;
    this.popup.classList.add(this.classes.closingClass);
    this.removeDialogListeners();
    this.restoreFocus();

    if (userDismissal && this.dataset.testMode !== 'true') {
      this.setCookie(this.dismissedCookieName, 7);
      this.setCookie(this.cookieName, 7);
    } else if (this.dataset.testMode === 'true' || this.alwaysShow) {
      this.removeCookie(this.cookieName);
    } else {
      this.setCookie(this.cookieName, this.dataset.expiry);
    }

    if (this.closeTimer !== null) window.clearTimeout(this.closeTimer);
    this.closeTimer = window.setTimeout(() => {
      this.popup?.classList.remove(this.classes.activeClass, this.classes.closingClass, this.classes.showImage);
      this.dialog?.setAttribute('aria-hidden', 'true');
      this.unlockBody();
      this.closeTimer = null;
    }, 500);
  }

  onDocumentKeydown(event) {
    if (!this.popup?.classList.contains(this.classes.activeClass)) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close(true);
      return;
    }

    if (event.key !== 'Tab' || !this.dialog) return;

    const focusable = Array.from(this.dialog.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    )).filter((element) => element.getClientRects().length > 0 && element.getAttribute('aria-hidden') !== 'true');

    if (focusable.length === 0) {
      event.preventDefault();
      this.dialog.focus({ preventScroll: true });
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || !this.dialog.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !this.dialog.contains(document.activeElement))) {
      event.preventDefault();
      first.focus();
    }
  }

  onDocumentFocusIn(event) {
    if (this.popup?.classList.contains(this.classes.activeClass) && !this.dialog?.contains(event.target)) {
      this.querySelector('.popup-close')?.focus({ preventScroll: true });
    }
  }

  removeDialogListeners() {
    document.removeEventListener('keydown', this.onDocumentKeydown);
    document.removeEventListener('focusin', this.onDocumentFocusIn);
  }

  restoreFocus() {
    const target = this.previousFocus;
    this.previousFocus = null;
    if (target?.isConnected && typeof target.focus === 'function') {
      target.focus({ preventScroll: true });
    }
  }

  unlockBody() {
    if (this.bodyLockOwned) document.body.classList.remove(this.classes.lockClass);
    if (this.bodyClassOwned) document.body.classList.remove(this.classes.bodyClass);
    this.bodyLockOwned = false;
    this.bodyClassOwned = false;
  }

  getCookie(name) {
    const match = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
    return match ? match[2] : null;
  }

  setCookie(name, expiry) {
    document.cookie = `${name}=true; max-age=${24 * Number.parseInt(expiry, 10) * 60 * 60}; path=/`;
  }

  setSubmittedCookie() {
    document.cookie = `${this.submittedCookieName}=true; max-age=315360000; path=/`;
  }

  removeCookie(name) {
    document.cookie = `${name}=; max-age=0; path=/`;
  }
}

if (!customElements.get('promo-popup')) {
  customElements.define('promo-popup', PromoPopup);
}
